import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Spinner, Breadcrumb, Form, Button, Alert, Modal } from 'react-bootstrap';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaReply, FaClock, FaEye, FaRobot, FaEdit, FaExclamationTriangle, FaClock as FaPending, FaTrash, FaImage, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import DOMPurify from 'dompurify';
import './Community.css';
import { formatTimeAgo } from '../../utils/communityUtils';
import CommunityBadge from './shared/CommunityBadge';
import UpvoteButton from './shared/UpvoteButton';
import ReplyItem from './shared/ReplyItem';
import AISummaryBox from './shared/AISummaryBox';
import { useAuth } from '../../../contexts/AuthContext';

export default function ThreadDetail() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { currentUser, token } = useAuth();
  const [thread, setThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [replyImageUrl, setReplyImageUrl] = useState(null);
  const [uploadingReplyImage, setUploadingReplyImage] = useState(false);
  const [replyFeedbackModal, setReplyFeedbackModal] = useState({ show: false, type: 'success', message: '' });
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    const fetchThread = async () => {
      try {
        setLoading(true);
        const headers = currentUser ? { 'Authorization': `Bearer ${token}` } : {};

        // Fetch thread
        const threadRes = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/discussions/${threadId}`, { headers });
        const threadData = await threadRes.json();
        
        if (threadData.success) {
          setThread(threadData.discussion);
          
          // Fetch category name for breadcrumb
          const catRes = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/categories`);
          const catData = await catRes.json();
          if (catData.success) {
            const foundCat = catData.categories.find(c => c.id === threadData.discussion.communityId);
            setCategory(foundCat);
          }

          // Fetch replies
          const repRes = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/discussions/${threadId}/replies`, { headers });
          const repData = await repRes.json();
          if (repData.success) {
            setReplies(repData.replies);
          }
        }
      } catch (error) {
        console.error("Error fetching thread", error);
      } finally {
        setLoading(false);
      }
    };

    fetchThread();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  const fetchReplies = async () => {
    try {
      const headers = currentUser ? { 'Authorization': `Bearer ${token}` } : {};
      const repRes = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/discussions/${threadId}/replies`, { headers });
      const repData = await repRes.json();
      if (repData.success) {
        setReplies(repData.replies);
      }
    } catch (err) {
      console.error('Error re-fetching replies:', err);
    }
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !currentUser) return;

    setSubmittingReply(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/discussions/${threadId}/replies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          contentHtml: `<p>${replyText
            .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-warning hover-gold text-decoration-underline">$1</a>')
            .replace(/\n/g, '<br/>')}</p>`,
          imageUrls: replyImageUrl ? [replyImageUrl] : []
        })
      });

      const data = await response.json();
      if (data.success) {
        setReplyText('');
        setReplyImageUrl(null);
        if (data.status === 'published') {
          await fetchReplies();
        } else {
          setReplyFeedbackModal({
            show: true,
            type: 'success',
            message: 'Your reply has been submitted for moderation review. You will be notified once it is approved.'
          });
        }
      } else {
        setReplyFeedbackModal({
          show: true,
          type: 'error',
          message: data.error || 'Failed to submit reply'
        });
      }
    } catch (error) {
      console.error("Error submitting reply", error);
      setReplyFeedbackModal({
        show: true,
        type: 'error',
        message: 'A network error occurred. Please try again.'
      });
    } finally {
      setSubmittingReply(false);
    }
  };


  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/discussions/${threadId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setShowDeleteModal(false);
        setShowDeleteSuccessModal(true);
      } else {
        alert(data.error || 'Failed to delete post');
      }
    } catch (error) {
      console.error('Delete failed', error);
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="community-container d-flex justify-content-center align-items-center">
        <Spinner animation="border" variant="warning" />
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="community-container text-center py-5">
        <h2 className="text-white">Discussion not found</h2>
        <Link to="/community" className="community-link mt-3 d-inline-block">Return to Community Home</Link>
      </div>
    );
  }

  return (
    <div className="community-container">
      <Container>
        <Breadcrumb className="mb-4">
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/community", className: "text-warning text-decoration-none hover-gold fw-bold" }}>
            Community Home
          </Breadcrumb.Item>
          {category && (
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/community/c/${category.slug}`, className: "text-warning text-decoration-none hover-gold fw-bold" }}>
              {category.name}
            </Breadcrumb.Item>
          )}
          <Breadcrumb.Item active>
            <span className="text-white text-truncate d-inline-block" style={{ maxWidth: '300px', verticalAlign: 'bottom' }}>
              {thread.title}
            </span>
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Rejection / Pending banner for the author */}
        {thread.status === 'rejected' && currentUser && currentUser.uid === thread.authorId && (
          <Alert variant="danger" className="mb-4">
            <Alert.Heading><FaExclamationTriangle className="me-2" />Your post was not approved</Alert.Heading>
            {thread.rejectionReason && (
              <p className="mb-2">
                <strong>Reason from moderator:</strong> &ldquo;{thread.rejectionReason}&rdquo;
              </p>
            )}
            <p className="mb-3">Please edit your post to address the feedback above, then resubmit it for review.</p>
            <Button variant="warning" onClick={() => navigate(`/community/edit/${threadId}`)}>
              <FaEdit className="me-2" />Edit &amp; Resubmit
            </Button>
          </Alert>
        )}

        {/* Pending review banner for the author */}
        {thread.status === 'held_for_review' && currentUser && currentUser.uid === thread.authorId && (
          <Alert variant="info" className="mb-4">
            <FaPending className="me-2" />
            <strong>Your post is under review.</strong> It will be publicly visible once a moderator approves it. You will receive a notification.
          </Alert>
        )}

        <Row>
          <Col lg={9}>
            {/* Thread Header */}
            <div className="mb-4">
              <h1 className="text-white mb-3">{thread.title}</h1>
              
              <div className="d-flex align-items-center flex-wrap gap-3 pb-3 border-bottom border-dark">
                <div className="d-flex align-items-center gap-2">
                  <div 
                    className="rounded-circle d-flex align-items-center justify-content-center bg-secondary text-white"
                    style={{ width: '32px', height: '32px' }}
                  >
                    {thread.authorName ? thread.authorName.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <Link to={`/community/user/${thread.authorId}`} className="text-white text-decoration-none fw-bold">
                    {thread.authorName}
                  </Link>
                </div>
                
                {thread.authorCommunityRole && <CommunityBadge role={thread.authorCommunityRole} />}
                
                <span className="text-muted small"><FaClock className="me-1" /> {formatTimeAgo(thread.createdAt)}</span>
                <span className="text-muted small"><FaEye className="me-1" /> {thread.viewCount} views</span>
                
                {thread.lakeId && (
                  <Link to={`/community/lakes/${thread.lakeId}`} className="badge bg-secondary text-decoration-none">
                    {thread.lakeId.replace('lake_', '').replace(/_/g, ' ').toUpperCase()}
                  </Link>
                )}

                {/* Author actions — only visible to the post author */}
                {currentUser && currentUser.uid === thread.authorId && (
                  <div className="ms-auto d-flex gap-2">
                    {thread.status === 'rejected' && (
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => navigate(`/community/edit/${threadId}`)}
                      >
                        <FaEdit className="me-1" /> Edit
                      </Button>
                    )}
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => setShowDeleteModal(true)}
                    >
                      <FaTrash className="me-1" /> Delete
                    </Button>
                  </div>
                )}
              </div>
            </div>


            {/* Original Post Content */}
            <div className="d-flex gap-4 mb-5">
              <div className="d-none d-sm-block mt-2">
                <UpvoteButton discussionId={thread.id} initialCount={thread.upvoteCount} />
              </div>
              
              <div className="flex-grow-1">
                <div 
                  className="text-light mb-4" 
                  style={{ fontSize: '1.1rem', lineHeight: '1.7' }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(thread.contentHtml) }}
                />

                {/* Discussion Images */}
                {thread.imageUrls && thread.imageUrls.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mb-4">
                    {thread.imageUrls.map((url, i) => (
                      <img
                        key={i}
                        src={url}
                        alt={`Post image ${i + 1}`}
                        className="rounded"
                        style={{ maxWidth: '300px', maxHeight: '250px', objectFit: 'cover', cursor: 'pointer', border: '1px solid #333' }}
                        onClick={() => setLightboxImage(url)}
                      />
                    ))}
                  </div>
                )}
                
                <div className="d-sm-none mb-4 border-top border-dark pt-3">
                  <UpvoteButton discussionId={thread.id} initialCount={thread.upvoteCount} />
                </div>
              </div>
            </div>

            {/* AI Summary (if exists or requested) */}
            {(thread.aiSummary || generatingSummary) && (
              <AISummaryBox summary={thread.aiSummary} isLoading={generatingSummary} />
            )}

            {/* Replies Section */}
            <div className="mb-4 d-flex justify-content-between align-items-center">
              <h3 className="text-white mb-0">{replies.length} Replies</h3>
              

            </div>

            {replies.length > 0 ? (
              <div className="replies-list mb-5">
                {replies.map(reply => (
                  <ReplyItem 
                    key={reply.id} 
                    reply={reply} 
                    isNested={reply.parentReplyId != null}
                    onReplyClick={(id, name) => {
                      setReplyText(`@${name} `);
                      document.getElementById('reply-box').focus();
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="text-muted mb-5 pb-5 border-bottom border-dark">
                No replies yet.
              </div>
            )}

            {/* Reply Form */}
            {currentUser ? (
              thread.isLocked ? (
                <div className="alert alert-secondary bg-dark text-muted border-secondary">
                  This discussion is locked and no longer accepting replies.
                </div>
              ) : (
              <div className="reply-form-container mt-5">
                  <h4 className="text-white mb-3">Leave a Reply</h4>
                  <Form onSubmit={handleReplySubmit}>
                    <Form.Group className="mb-3">
                      <Form.Control 
                        id="reply-box"
                        as="textarea" 
                        rows={5} 
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Write your reply here..."
                        className="bg-dark text-light border-secondary"
                        required
                      />
                    </Form.Group>

                    {/* Reply Image Attachment */}
                    <div className="mb-3">
                      {replyImageUrl ? (
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <img src={replyImageUrl} alt="Reply attachment" className="rounded" style={{ maxWidth: '150px', maxHeight: '120px', objectFit: 'cover', border: '1px solid #444' }} />
                          <button
                            type="button"
                            onClick={() => setReplyImageUrl(null)}
                            style={{
                              position: 'absolute', top: '-6px', right: '-6px',
                              background: '#dc3545', border: 'none', borderRadius: '50%',
                              width: '22px', height: '22px', color: '#fff', fontSize: '12px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => document.getElementById('reply-image-input').click()}
                          disabled={uploadingReplyImage}
                        >
                          {uploadingReplyImage ? <Spinner size="sm" className="me-1" /> : <FaImage className="me-1" />}
                          {uploadingReplyImage ? 'Uploading...' : 'Attach Image'}
                        </Button>
                      )}
                      <input
                        id="reply-image-input"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) {
                            alert('Image must be under 5MB.');
                            return;
                          }
                          setUploadingReplyImage(true);
                          try {
                            const formData = new FormData();
                            formData.append('files', file);
                            const res = await fetch(`${process.env.REACT_APP_BASE_URL}/api/upload-community-images`, {
                              method: 'POST',
                              body: formData,
                            });
                            const data = await res.json();
                            if (data.success && data.file_names.length > 0) {
                              setReplyImageUrl(`${process.env.REACT_APP_BASE_URL}/images/${data.file_names[0]}`);
                            }
                          } catch (err) {
                            console.error('Reply image upload failed:', err);
                          } finally {
                            setUploadingReplyImage(false);
                            e.target.value = '';
                          }
                        }}
                      />
                    </div>

                    <div className="text-end">
                      <Button variant="warning" type="submit" disabled={submittingReply || !replyText.trim()}>
                        {submittingReply ? <Spinner size="sm" className="me-2"/> : <FaReply className="me-2"/>}
                        Post Reply
                      </Button>
                    </div>
                  </Form>
                </div>
              )
            ) : (
              <div className="alert alert-secondary bg-dark text-muted border-secondary d-flex justify-content-between align-items-center">
                <span>You must be logged in to reply to this discussion.</span>
                <Link to="/auth" className="btn btn-sm btn-outline-warning">Log In</Link>
              </div>
            )}
            
          </Col>
          
          <Col lg={3} className="d-none d-lg-block">
            {/* Sidebar (similar threads, rules, etc) */}
            <div className="community-card sticky-top" style={{ top: '100px' }}>
              <h5 className="text-white mb-3">Community Rules</h5>
              <ul className="text-muted small ps-3 mb-0">
                <li className="mb-2">Be respectful to fellow anglers.</li>
                <li className="mb-2">No contact sharing (phone, email). Use BBR messaging.</li>
                <li className="mb-2">No off-platform booking attempts.</li>
                <li>Stay on topic for the category.</li>
              </ul>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        contentClassName="bg-dark border border-secondary"
      >
        <Modal.Body className="text-center py-5 px-4">
          <FaTrash style={{ fontSize: '3rem', color: '#dc3545' }} className="mb-4" />
          <h4 className="text-white mb-3">Delete this post?</h4>
          <p className="text-muted mb-4">
            This will remove your post from public view. Only moderators will be able to see it.
          </p>
          <div className="d-flex gap-3 justify-content-center">
            <Button variant="outline-light" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Spinner size="sm" className="me-2" /> : <FaTrash className="me-2" />}
              Yes, Delete It
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Delete Success Modal */}
      <Modal
        show={showDeleteSuccessModal}
        onHide={() => navigate('/community', { replace: true })}
        centered
        contentClassName="bg-dark border border-secondary"
      >
        <Modal.Body className="text-center py-5 px-4">
          <div className="mb-4">
            <FaCheckCircle style={{ fontSize: '4rem', color: '#198754' }} />
          </div>
          <h4 className="text-white mb-3">Post Deleted</h4>
          <p className="text-muted mb-4">
            Your post has been successfully removed.
          </p>
          <Button variant="outline-light" onClick={() => navigate('/community', { replace: true })} className="px-4 py-2">
            Okay
          </Button>
        </Modal.Body>
      </Modal>

      {/* Reply Feedback Modal */}
      <Modal
        show={replyFeedbackModal.show}
        onHide={() => setReplyFeedbackModal(prev => ({ ...prev, show: false }))}
        centered
        contentClassName="bg-dark border border-secondary"
      >
        <Modal.Body className="text-center py-5 px-4">
          {replyFeedbackModal.type === 'success' ? (
            <FaCheckCircle style={{ fontSize: '3.5rem', color: '#f0a500' }} className="mb-4" />
          ) : (
            <FaExclamationCircle style={{ fontSize: '3.5rem', color: '#dc3545' }} className="mb-4" />
          )}
          <h4 className="text-white mb-3">
            {replyFeedbackModal.type === 'success' 
              ? (replyFeedbackModal.status === 'published' ? 'Reply Published!' : 'Flagged by AI') 
              : 'Something went wrong'}
          </h4>
          <p className="text-muted mb-4" style={{ lineHeight: '1.6' }}>
            {replyFeedbackModal.message}
          </p>
          <Button
            variant={replyFeedbackModal.type === 'success' ? 'warning' : 'outline-secondary'}
            onClick={() => setReplyFeedbackModal(prev => ({ ...prev, show: false }))}
          >
            Got It
          </Button>
        </Modal.Body>
      </Modal>

      {/* Image Lightbox */}
      <Modal
        show={!!lightboxImage}
        onHide={() => setLightboxImage(null)}
        centered
        size="lg"
        contentClassName="bg-dark border-0"
      >
        <Modal.Body className="p-0 text-center">
          <img
            src={lightboxImage}
            alt="Full size"
            style={{ width: '100%', maxHeight: '85vh', objectFit: 'contain' }}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}
